import { useInfiniteQuery } from '@tanstack/react-query';
import { http } from 'services/http';
import { AUDIT_LOGS_GET_MANY } from 'api/query-keys.ts';
import type { TAuditLogAction, TAuditLogEntityType, TAuditLogRes } from 'api/swagger';
import { prepareCursorPagination, type IWithCursorPagination } from 'shared/utils/pagination.ts';

const AUDIT_LOG_DEFAULT_LIMIT = 25;

interface IAuditLogFilters {
  actions?: TAuditLogAction[];
  userId?: number;
}

interface IParams {
  entityType: TAuditLogEntityType;
  entityId: number;
  limit?: number;
  filters?: IAuditLogFilters;
  enabled?: boolean;
}

export const useGetAuditLogs = (params: IParams) => {
  const { entityType, entityId, limit = AUDIT_LOG_DEFAULT_LIMIT, filters, enabled = true } = params;

  return useInfiniteQuery<IWithCursorPagination<TAuditLogRes[]>>({
    queryKey: [AUDIT_LOGS_GET_MANY, { entityType, entityId, limit, filters }],
    queryFn: async ({ pageParam }) => {
      const qs = new URLSearchParams({ limit: String(limit) });

      if (pageParam) qs.set('beforeId', String(pageParam));
      filters?.actions?.forEach((action, index) => qs.set(`filter[actions][${index}]`, action));
      if (filters?.userId) qs.set('filter[userId]', String(filters.userId));

      const { data, headers } = await http.get<TAuditLogRes[]>(
        `/v1/entities/${entityType}/${entityId}/audit-log?${qs.toString()}`,
      );

      return { data, pagination: prepareCursorPagination(headers) };
    },
    getNextPageParam: (lastPage) => {
      if (!lastPage.pagination?.hasMore) return undefined;

      return lastPage.pagination.cursor ?? undefined;
    },
    retry: false,
    refetchOnWindowFocus: false,
    enabled,
  });
};
